import { DiffLineContainer, type DiffLinePlacement } from "@ext/git/core/Diff/components/DiffLine/DiffLineContainer";
import { DiffLineMarker } from "@ext/git/core/Diff/components/DiffLine/DiffLineMarker";
import { DiffLinePopoverContent } from "@ext/git/core/Diff/components/DiffLine/DiffLinePopover";
import ProseMirrorDiffLineContent from "@ext/git/core/Diff/components/ProseMirrorDiffLineContent";
import { useHoverPopover } from "@ext/git/core/Diff/logic/hooks/useHoverPopover";
import type { TreeReadScope } from "@ext/git/core/GitCommands/model/GitCommandsModel";
import t from "@ext/localization/locale/translate";
import type { JSONContent } from "@tiptap/core";
import { Popover, PopoverAnchor } from "@ui-kit/Popover";
import { Component, type MouseEvent, type ReactNode, useState } from "react";

class PreviousVersionBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
	state = { failed: false };

	static getDerivedStateFromError() {
		return { failed: true };
	}

	render() {
		if (this.state.failed)
			return <div className="p-2 text-xs opacity-70">{t("diff.previous-version-unavailable")}</div>;
		return this.props.children;
	}
}

const TALL_LINE = 200;

const usePointerAnchor = (height: number) => {
	const [pointerTop, setPointerTop] = useState(0);
	const isTall = height > TALL_LINE;
	return {
		style: isTall ? { top: pointerTop, height: 1 } : { top: 0, height: "100%" },
		pointAt: (event: MouseEvent<HTMLElement>) => {
			if (isTall) setPointerTop(event.clientY - event.currentTarget.getBoundingClientRect().top);
		},
	};
};

interface ModifiedDiffLineProps {
	placement: DiffLinePlacement;
	oldContent: JSONContent;
	newContent: JSONContent;
	oldScope: TreeReadScope;
	articlePath: string;
	onDiscard?: () => void;
}

export const ModifiedDiffLine = ({
	placement,
	oldContent,
	newContent,
	oldScope,
	articlePath,
	onDiscard,
}: ModifiedDiffLineProps) => {
	const previousVersion = useHoverPopover();
	const anchor = usePointerAnchor(placement.height);

	return (
		<Popover onOpenChange={previousVersion.setOpen} open={previousVersion.open}>
			<DiffLineContainer interactive placement={placement} type="modified">
				<div
					className="relative h-[inherit] w-full"
					{...previousVersion.triggerProps}
					onMouseEnter={(event) => {
						anchor.pointAt(event);
						previousVersion.triggerProps.onMouseEnter();
					}}
				>
					<PopoverAnchor asChild>
						<div className="pointer-events-none absolute inset-x-0" style={anchor.style} />
					</PopoverAnchor>
					<DiffLineMarker height={placement.height} type="modified" />
				</div>
			</DiffLineContainer>
			<DiffLinePopoverContent {...previousVersion.contentProps}>
				<PreviousVersionBoundary>
					<ProseMirrorDiffLineContent
						articlePath={articlePath}
						newContent={newContent}
						oldContent={oldContent}
						oldScope={oldScope}
						onDiscard={
							onDiscard &&
							(() => {
								onDiscard();
								previousVersion.close();
							})
						}
						type="modified"
					/>
				</PreviousVersionBoundary>
			</DiffLinePopoverContent>
		</Popover>
	);
};
